import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/utils";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import renovateYourStore from "@faire/web-api--source/routes/www/open-with-faire/academy/renovate-your-store";
import { lazy } from "react";
import * as React from "react";

const LazyPlanningYourLayout = lazy(() =>
  import(
    /* webpackChunkName: "renovate-your-store-planning-your-layout" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/RenovateYourStore/__internal__/PlanningYourLayout"
  ).then((m) => ({ default: m.PlanningYourLayout }))
);
const LazyAssessingNecessaryWork = lazy(() =>
  import(
    /* webpackChunkName: "renovate-your-store-assessing-necessary-work" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/RenovateYourStore/__internal__/AssessingNecessaryWork"
  ).then((m) => ({ default: m.AssessingNecessaryWork }))
);
const LazyBudgetingForARenovation = lazy(() =>
  import(
    /* webpackChunkName: "renovate-your-store-budgeting-for-a-renovation" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/RenovateYourStore/__internal__/BudgetingForARenovation"
  ).then((m) => ({ default: m.BudgetingForARenovation }))
);
const LazyPrebuildLaborAndMaterials = lazy(() =>
  import(
    /* webpackChunkName: "renovate-your-store-prebuild-labor-and-materials" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/RenovateYourStore/__internal__/PrebuildLaborAndMaterials"
  ).then((m) => ({ default: m.PrebuildLaborAndMaterials }))
);
const LazyPostBuildInspections = lazy(() =>
  import(
    /* webpackChunkName: "renovate-your-store-post-build-inspections" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/RenovateYourStore/__internal__/PostBuildInspections"
  ).then((m) => ({ default: m.PostBuildInspections }))
);
const LazyFurnishings = lazy(() =>
  import(
    /* webpackChunkName: "renovate-your-store-furnishings" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/RenovateYourStore/__internal__/Furnishings"
  ).then((m) => ({ default: m.Furnishings }))
);

const C1 = "Planning your layout";
const PLANNING_YOUR_LAYOUT: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.PLANNING_YOUR_LAYOUT,
  title: C1,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyPlanningYourLayout,
};

const C2 = "Assessing necessary work";
const ACCESSING_NECESSARY_WORK: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.ASSESSING_NECESSARY_WORK,
  title: C2,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyAssessingNecessaryWork,
};

const C3 = "Budgeting for a renovation";
const BUDGETING_FOR_A_RENOVATION: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.BUDGETING_RENOVATIONS,
  title: C3,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C3),
  ChapterComponent: LazyBudgetingForARenovation,
};

const C4 = "Pre-build: Labor and materials";
const LABOR_AND_MATERIALS: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.PRE_BUILD_LABOR_AND_MATERIALS,
  title: C4,
  readTimeMinutes: 3,
  titleHash: convertTitleToHash(C4),
  ChapterComponent: LazyPrebuildLaborAndMaterials,
};

const C5 = "Post-build: Inspections";
const INSPECTIONS: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.POST_BUILD_INSPECTIONS,
  title: C5,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C5),
  ChapterComponent: LazyPostBuildInspections,
};

const C6 = "Furnishings";
const FURNISHINGS: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.FURNISHINGS,
  title: C6,
  readTimeMinutes: 1,
  titleHash: convertTitleToHash(C6),
  ChapterComponent: LazyFurnishings,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  PLANNING_YOUR_LAYOUT,
  ACCESSING_NECESSARY_WORK,
  BUDGETING_FOR_A_RENOVATION,
  LABOR_AND_MATERIALS,
  INSPECTIONS,
  FURNISHINGS,
];

export const RENOVATE_YOUR_STORE: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.RENOVATE_YOUR_STORE,
  title: "Renovate your store",
  description:
    "Transform your retail space into your dream store, without a hitch.",
  cardSummary: "Transform your space into your dream store, without a hitch.",
  guideSummary: (
    <>
      Completing a renovation and passing inspection is one of the biggest and
      most challenging steps in the journey to opening day. You’ll need to
      balance a budget and building codes with the strategic selection of
      figures and finishes that reflect your brand’s aesthetic. You’ll also need
      a talented crew of contractors and other tradespeople to get your
      renovation done. Choosing the right ones can mean completing your project
      efficiently, within budget, and to your satisfaction. Once inspections are
      passed, functional and decorative furnishings are the last piece of the
      puzzle—ensuring your store feels personal, inviting, and easy to shop.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/2b38bde59247db172a8adf411217835488629fbf9c17304f9485e149e74a679e.png",
  featuredImageAltText: "Overhead sign outside store called post.script.",
  backgroundImage:
    "https://cdn.faire.com/fastly/c922f29530664a8643bcdc1bc5c8060c54a6aa00fa95073ef3ef323d264e7954.jpeg",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: renovateYourStore(),
  titleTag: "How to renovate & furnish your retail store",
};
